/** Console v0.5: Organisations and one organisation's workspace (design brief v0.5 s.5, the Organisations and Client
 * mock-ups), on session A's routes (#386, contracts s.17) and the work items (#375). A practice lists the
 * organisations each person may see; a company shows itself. Scope, adding and the limits are the server's. */

import { ApiError } from "../api.js";
import type { ScreenContext } from "./app.js";
import { SCREENS } from "./app.js";
import { ASSURANCE_NOTE, FINANCING_LINE, FINANCING_SMALL_PRINT, SERVICES, TAX_NOTE } from "./copy.js";
import { dueLabel } from "./format.js";
import { fetchItems } from "./items-api.js";
import type { InboxTab, ItemRow } from "./items-api.js";
import { addOrganisation, fetchOrganisation, fetchOrganisations, setPinned } from "./orgs-api.js";
import type { OrgList, OrgRow } from "./orgs-api.js";
import { ORGS_COPY, ORG_TABS, placesLeft, togglePin } from "./orgs-logic.js";
import type { OrgTab } from "./orgs-logic.js";
import { abnValid } from "./admin-logic.js";
import { formatAbn, initials, routePath } from "./routes.js";
import { avatar, button, card, chip, emptyState, figure, h, legalFooter, loading, notice, openDialog, pageHead, statTile, table, tabs, tag, uid } from "./ui.js";

function messageOf(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

function xeroTag(o: OrgRow): HTMLElement {
  return tag(o.xero.connected ? "Xero connected" : "Not connected", o.xero.connected ? "good" : "neutral");
}

// ---- The list ----

function organisations(ctx: ScreenContext): () => void {
  const company = ctx.me.account?.type === "company";
  let data: OrgList | null = null;
  let error: string | null = null;
  let query = "";
  let flash: { text: string; ok: boolean } | null = null;
  let focusFlash = false;
  let gone = false;
  let timer: ReturnType<typeof setTimeout> | null = null;

  async function load(): Promise<void> {
    try {
      data = await fetchOrganisations(query);
      error = null;
    } catch (err) {
      error = messageOf(err, "Preekie couldn't load the organisations. Try again in a few minutes.");
    }
    draw();
  }

  function openAdd(): void {
    const name = h("input", "pk5-input");
    name.maxLength = 120;
    const nameId = uid("pk5-org-name");
    name.id = nameId;
    const abn = h("input", "pk5-input");
    abn.inputMode = "numeric";
    abn.placeholder = "11 digits (optional)";
    const abnId = uid("pk5-org-abn");
    abn.id = abnId;
    const problem = h("p", "pk5-form-error");
    problem.setAttribute("role", "alert");
    problem.hidden = true;
    const show = (t: string) => {
      problem.textContent = t;
      problem.hidden = false;
    };
    const nl = h("label", "pk5-label", "Legal name");
    nl.htmlFor = nameId;
    const al = h("label", "pk5-label", "ABN");
    al.htmlFor = abnId;
    openDialog(
      ORGS_COPY.practiceAdd,
      [h("div", "pk5-field", nl, name), h("div", "pk5-field", al, abn), h("p", "pk5-muted", "Connect it to Xero afterwards, on Connections."), problem],
      [
        { label: "Cancel", variant: "secondary", safe: true, onClick: (close) => close() },
        {
          label: "Add organisation",
          variant: "primary",
          onClick: async (close, btn) => {
            if (!name.value.trim()) return show("Enter the organisation's legal name.");
            if (abn.value.trim() && !abnValid(abn.value)) return show("That ABN isn't valid. Check the 11 digits, or leave it blank.");
            btn.disabled = true;
            try {
              const r = await addOrganisation(name.value, abn.value);
              flash = { text: `${r.organisation.name} added.`, ok: true };
              focusFlash = true;
              close();
              await load();
            } catch (err) {
              // 409 limit_reached: the server's exact words, dialog stays open.
              show(messageOf(err, "The organisation wasn't added. Try again in a few minutes."));
              btn.disabled = false;
            }
          },
        },
      ]
    );
    name.focus();
  }

  function draw(): void {
    if (gone) return;
    const actions = data?.can_add && !company ? [button(ORGS_COPY.practiceAdd, "primary", openAdd)] : [];
    const parts: Node[] = [pageHead("Organisations", company ? ORGS_COPY.companyIntro : ORGS_COPY.practiceIntro, actions)];
    if (flash) {
      const n = notice(flash.text, flash.ok ? "status" : "alert");
      n.tabIndex = -1;
      n.classList.add("pk5-flash");
      parts.push(n);
    }
    if (error) parts.push(notice(error, "alert"));
    else if (!data) parts.push(loading());
    else {
      const orgs = data.organisations;
      const attention = orgs.filter((o) => o.status === "Exception" || o.status === "Awaiting review").length;
      const left = placesLeft(data.limits.organisations);
      const lim = ctx.me.account?.limits;
      const stats = h(
        "section",
        "pk5-stats",
        statTile("Organisations", String(orgs.length)),
        statTile("Need attention", String(attention)),
        statTile("Open items", String(orgs.reduce((n, o) => n + o.open_items, 0))),
        ...(company
          ? lim
            ? [statTile("Users", `${lim.users.used}/${lim.users.max}`)]
            : []
          : left !== null
            ? [statTile("Plan places left", String(left))]
            : [])
      );
      stats.setAttribute("aria-label", "Summary");
      parts.push(stats);

      const search = h("input", "pk5-input pk5-search-input");
      search.type = "search";
      search.placeholder = "Name or ABN";
      search.value = query;
      search.setAttribute("aria-label", "Search organisations");
      search.addEventListener("input", () => {
        query = search.value;
        if (timer) clearTimeout(timer);
        timer = setTimeout(() => void load().then(() => (ctx.root.querySelector(".pk5-search-input") as HTMLInputElement | null)?.focus()), 250);
      });
      const rows = orgs.map((o) => {
        const link = h("a", "pk5-person", avatar(initials(o.name)), h("span", "pk5-person-text", h("span", "pk5-person-name", o.name), o.abn ? figure(`ABN ${formatAbn(o.abn)}`) : h("span", "pk5-muted", "No ABN")));
        link.href = routePath({ kind: "organisation", id: o.organisation_id });
        return [link, xeroTag(o), figure(String(o.open_items)), o.status ? chip(o.status) : h("span", "pk5-muted", "Nothing open")];
      });
      const id = uid("pk5-orgs-h");
      const heading = h("h2", "pk5-section-title", company ? ORGS_COPY.companyList : ORGS_COPY.practiceList);
      heading.id = id;
      const section = h(
        "section",
        "pk5-org-section",
        h("div", "pk5-card-head", heading, company ? null : search),
        orgs.length
          ? table(company ? ORGS_COPY.companyList : ORGS_COPY.practiceList, [{ label: "Organisation" }, { label: "Ledger" }, { label: "Open items", num: true }, { label: "Most urgent" }], rows)
          : card(undefined, emptyState(query ? ORGS_COPY.noMatch : ORGS_COPY.none)),
        h("p", "pk5-muted", company ? ORGS_COPY.companyFoot : ORGS_COPY.practiceFoot)
      );
      section.setAttribute("aria-labelledby", id);
      parts.push(section);
    }
    parts.push(legalFooter());
    ctx.root.replaceChildren(...parts);
    if (focusFlash) {
      focusFlash = false;
      (ctx.root.querySelector(".pk5-flash") as HTMLElement | null)?.focus();
    }
  }

  draw();
  void load();
  return () => {
    gone = true;
    if (timer) clearTimeout(timer);
  };
}

// ---- One organisation's workspace ----

const TAB_NOTES: Partial<Record<OrgTab, string>> = {
  tax: TAX_NOTE,
  assurance: "Internal review · not a certification",
  financing: "Via lending partners · not financial product advice",
};

function workspace(ctx: ScreenContext): () => void {
  const id = ctx.route.kind === "organisation" ? ctx.route.id : "";
  let org: (OrgRow & { services: Array<{ service: string; open: number }> }) | null = null;
  let error: string | null = null;
  let items: ItemRow[] | null = null;
  let tab: OrgTab = "overview";
  let pinned = (ctx.me.pinned_organisations ?? []).map((p) => p.organisation_id);
  let pinMsg: string | null = null;
  let gone = false;

  async function load(): Promise<void> {
    try {
      org = (await fetchOrganisation(id)).organisation;
      ctx.setCrumbs([{ label: "Organisations", href: "/organisations" }, { label: org.name }]);
      document.title = `${org.name} · Preekie`;
    } catch (err) {
      // 404 for another account's, out of reach or unknown: the same answer.
      error = messageOf(err, "Preekie couldn't load this organisation.");
    }
    draw();
    if (!org) return;
    const tabsToLoad: InboxTab[] = ["awaiting_you", "exceptions", "requested", "done"];
    try {
      const lists = await Promise.all(tabsToLoad.map((t) => fetchItems(t, { organisation_id: id })));
      items = lists.flatMap((l) => l.items);
    } catch {
      items = [];
    }
    draw();
  }

  async function pin(): Promise<void> {
    const next = togglePin(pinned, id);
    if (!next) {
      pinMsg = ORGS_COPY.pinLimit;
      return draw();
    }
    try {
      const r = await setPinned(next);
      pinned = r.pinned_organisations.map((p) => p.organisation_id);
      ctx.me.pinned_organisations = r.pinned_organisations;
      pinMsg = pinned.includes(id) ? "Pinned to the sidebar." : "Unpinned.";
    } catch (err) {
      pinMsg = messageOf(err, "Pinned organisations weren't saved.");
    }
    draw();
  }

  function serviceCard(service: string): HTMLElement {
    const def = SERVICES.find((s) => s.service === service)!;
    const open = org?.services.find((s) => s.service === service)?.open ?? 0;
    const c = h(
      "article",
      "pk5-service",
      h("div", "pk5-service-head", h("h3", "pk5-service-name", def.name), h("span", "pk5-muted", def.tag)),
      h("div", "pk5-service-big", figure(String(open)), h("span", "pk5-muted", open === 1 ? "open item" : "open items"))
    );
    if (service === "tax") c.append(h("p", "pk5-service-note", TAX_NOTE));
    if (service === "assurance") c.append(h("p", "pk5-service-note", ASSURANCE_NOTE));
    if (service === "financing") {
      const mail = h("a", "pk5-link", "Discuss with us");
      mail.href = "mailto:hello@preekie.com?subject=Discuss%20with%20Preekie";
      c.append(h("p", "pk5-service-note", FINANCING_LINE), mail, h("p", "pk5-small-print", FINANCING_SMALL_PRINT));
    }
    return c;
  }

  function serviceList(service: string): Node {
    if (items === null) return loading();
    const mine = items.filter((i) => i.service === service);
    if (!mine.length) return card(undefined, emptyState("Nothing here yet.", service === "financing" ? FINANCING_LINE : undefined));
    return table(
      ORG_TABS.find((t) => t.id === service)!.label,
      [{ label: "Work" }, { label: "Status" }, { label: "Due" }],
      mine.map((i) => {
        const a = h("a", null, i.task);
        a.href = `/inbox?item=${encodeURIComponent(i.item_id)}`;
        return [a, chip(i.status_label), figure(dueLabel(i.due, new Date(), ctx.timeZone))];
      })
    );
  }

  function draw(): void {
    if (gone) return;
    if (error || !org) {
      ctx.root.replaceChildren(pageHead("Organisations", null), error ? notice(error, "alert") : loading(), legalFooter());
      return;
    }
    const o = org;
    const isPinned = pinned.includes(id);
    const pinBtn = button(isPinned ? "Unpin" : "Pin to sidebar", "secondary", () => void pin());
    pinBtn.setAttribute("aria-pressed", isPinned ? "true" : "false");
    const h1 = h("h1", "pk5-title", o.name);
    h1.id = "pk5-title";
    h1.tabIndex = -1;
    const head = h(
      "div",
      "pk5-page-head",
      h(
        "div",
        "pk5-org-head",
        avatar(initials(o.name)),
        h("div", null, h1, h("div", "pk5-org-meta", o.abn ? figure(`ABN ${formatAbn(o.abn)}`) : h("span", "pk5-muted", "No ABN"), xeroTag(o), o.status ? chip(o.status) : null))
      ),
      h("div", "pk5-head-actions", pinBtn)
    );
    const panelId = uid("pk5-org-panel");
    const panel = h("div", "pk5-tab-panel");
    panel.id = panelId;
    panel.setAttribute("role", "tabpanel");
    if (tab === "overview") panel.append(h("div", "pk5-service-grid", ...["accounting", "tax", "assurance", "financing"].map(serviceCard)));
    else {
      const note = TAB_NOTES[tab];
      panel.append(...(note ? [h("p", "pk5-muted", note)] : []), serviceList(tab));
    }
    const recent = items
      ? [...items].sort((a, b) => b.updated_at.localeCompare(a.updated_at)).slice(0, 6)
      : null;
    const activity = h(
      "aside",
      "pk5-card pk5-activity",
      h("h2", "pk5-section-title", "Recent work"),
      ...(recent === null
        ? [loading()]
        : recent.length
          ? recent.map((i) => h("p", "pk5-activity-line", h("span", null, i.task), h("span", "pk5-muted", `${i.status_label} · ${dueLabel(i.due, new Date(), ctx.timeZone)}`)))
          : [h("p", "pk5-muted", "No work for this organisation yet.")])
    );
    activity.setAttribute("aria-label", "Recent work");
    ctx.root.replaceChildren(
      head,
      ...(pinMsg ? [notice(pinMsg, pinMsg === ORGS_COPY.pinLimit ? "alert" : "status")] : []),
      tabs(
        "Organisation areas",
        ORG_TABS.map((t) => ({ id: t.id, label: t.label })),
        tab,
        (t) => {
          tab = t;
          draw();
        },
        panelId
      ),
      h("div", "pk5-org-split", panel, activity),
      legalFooter()
    );
  }

  draw();
  void load();
  return () => {
    gone = true;
  };
}

SCREENS.organisations = organisations;
SCREENS.organisation = workspace;

